import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://static.readdy.ai/image/b493beaa830f64c0e7708d562b6b5fcb/da713bd2611879be8599d7bc969c0c05.jpeg" 
                alt="Logo" 
                className="h-12 w-auto object-contain"
              />
              <span className="text-xl font-bold text-gray-900">フランチャイズ営業支援</span>
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors cursor-pointer">サービス特徴</a>
              <a href="#results" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors cursor-pointer">実績</a>
              <a href="#plans" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors cursor-pointer">料金プラン</a>
              <a href="#faq" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors cursor-pointer">FAQ</a>
              <a href="#contact" className="px-6 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-full hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap">お問い合わせ</a>
            </div>

            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 cursor-pointer"
            >
              <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-3">
              <a href="#features" className="block text-sm font-medium text-gray-700 cursor-pointer">サービス特徴</a>
              <a href="#results" className="block text-sm font-medium text-gray-700 cursor-pointer">実績</a>
              <a href="#plans" className="block text-sm font-medium text-gray-700 cursor-pointer">料金プラン</a>
              <a href="#faq" className="block text-sm font-medium text-gray-700 cursor-pointer">FAQ</a>
              <a href="#contact" className="block text-sm font-medium text-gray-700 cursor-pointer">お問い合わせ</a>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/457bd1c2d8bf04e111d40c1eec3da99a.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 w-full">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-block px-4 py-2 bg-amber-500/20 rounded-full">
                <span className="text-amber-400 text-sm font-medium">フランチャイズ本部経営者の方へ</span>
              </div>
              
              <h1 className="space-y-2">
                <div className="text-5xl md:text-6xl font-light text-white leading-tight">広告とセールスの統合で</div>
                <div className="text-5xl md:text-6xl font-bold text-white leading-tight">加盟店開発を劇的に加速させる</div>
              </h1>
              
              <p className="text-xl text-gray-300 leading-relaxed">
                Meta/Google広告 × キーエンス式高密度セールス<br />
                = アポ率84%の実績
              </p>
              
              <a href="#contact" className="inline-flex items-center gap-3 px-8 py-4 bg-orange-500 text-white font-semibold rounded-full hover:bg-orange-600 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap">
                <span className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                  <i className="ri-phone-line text-orange-500 text-xl"></i>
                </span>
                <span>無料相談を予約する</span>
                <i className="ri-arrow-right-up-line text-xl"></i>
              </a>
            </div>

            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional business team celebrating success in modern office handshake and collaboration scene bright natural lighting corporate achievement moment diverse business professionals working together clean simple background"
                  alt="Success Story"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-8 left-8">
                  <div className="text-5xl font-bold text-white mb-2">15店舗</div>
                  <div className="text-lg text-white/90">2ヶ月で加盟達成</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="relative py-32 bg-gradient-to-br from-gray-800 to-gray-900">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(https://readdy.ai/api/search-image?query=stressed%20business%20person%20in%20office%20contemplating%20challenges%20dark%20moody%20atmosphere%20professional%20workspace%20with%20documents%20and%20laptop%20corporate%20pressure%20and%20decision%20making%20scene%20minimalist%20background&width=1920&height=1080&seq=problem-bg-001&orientation=landscape)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}></div>
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-16 tracking-wide">
            こんな悩み、ありませんか？
          </h2>
          
          <div className="space-y-8 mb-16">
            <div className="flex items-start gap-4 text-left bg-white/5 backdrop-blur-sm p-6 rounded-2xl">
              <i className="ri-close-circle-line text-3xl text-red-400 flex-shrink-0 mt-1"></i>
              <p className="text-xl text-white font-light leading-relaxed">
                アントレ等のポータルに月額数十万円払っても、質の低いリードばかり...
              </p>
            </div>
            
            <div className="flex items-start gap-4 text-left bg-white/5 backdrop-blur-sm p-6 rounded-2xl">
              <i className="ri-close-circle-line text-3xl text-red-400 flex-shrink-0 mt-1"></i>
              <p className="text-xl text-white font-light leading-relaxed">
                広告代理店は「リード数」しか見ず、成約まで責任を持たない
              </p>
            </div>
            
            <div className="flex items-start gap-4 text-left bg-white/5 backdrop-blur-sm p-6 rounded-2xl">
              <i className="ri-close-circle-line text-3xl text-red-400 flex-shrink-0 mt-1"></i>
              <p className="text-xl text-white font-light leading-relaxed">
                営業代行に依頼しても、フランチャイズ特有の商談ノウハウがない
              </p>
            </div>
          </div>
          
          <p className="text-3xl text-white font-semibold mb-12 leading-relaxed">
            投資対効果が見えない加盟店開発に、<br />限界を感じていませんか？
          </p>
          
          <a href="#solution" className="inline-flex items-center gap-3 px-8 py-4 bg-gray-900 text-white font-semibold rounded-full hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap">
            <span className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
              <i className="ri-check-line text-white text-xl"></i>
            </span>
            <span>解決策を見る</span>
            <i className="ri-arrow-right-up-line text-xl"></i>
          </a>
        </div>
      </section>

      {/* Comparison Section */}
      <section id="solution" className="py-32 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <p className="text-sm font-serif text-gray-500 mb-4">Solution Comparison</p>
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-2">
              一般的なサービスとの
            </h2>
            <h2 className="text-5xl md:text-6xl font-serif italic text-gray-900">
              決定的な違い
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <div className="h-64 bg-rose-100 flex items-center justify-center p-8">
                <i className="ri-megaphone-line text-7xl text-rose-400"></i>
              </div>
              <div className="p-6 bg-white">
                <h3 className="text-lg font-bold text-gray-900 mb-2">リード獲得</h3>
                <p className="text-sm text-gray-600">一般：数重視<br />当社：成約見込み重視</p>
              </div>
            </div>

            <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <div className="h-64 bg-emerald-100 flex items-center justify-center p-8">
                <i className="ri-user-voice-line text-7xl text-emerald-400"></i>
              </div>
              <div className="p-6 bg-white">
                <h3 className="text-lg font-bold text-gray-900 mb-2">営業プロセス</h3>
                <p className="text-sm text-gray-600">一般：分断<br />当社：広告〜成約まで一気通貫</p>
              </div>
            </div>

            <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <div className="h-64 bg-gray-100 flex items-center justify-center p-8">
                <i className="ri-target-line text-7xl text-gray-400"></i>
              </div>
              <div className="p-6 bg-white">
                <h3 className="text-lg font-bold text-gray-900 mb-2">成果定義</h3>
                <p className="text-sm text-gray-600">一般：CPA<br />当社：実際の加盟契約数</p>
              </div>
            </div>

            <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <div className="h-64 bg-amber-100 flex items-center justify-center p-8">
                <i className="ri-money-dollar-circle-line text-7xl text-amber-500"></i>
              </div>
              <div className="p-6 bg-white">
                <h3 className="text-lg font-bold text-gray-900 mb-2">料金体系</h3>
                <p className="text-sm text-gray-600">一般：固定費のみ<br />当社：成果報酬対応可</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-20 text-center">
            当社だけの<span className="inline-block mx-2">🔥</span>3つの強み
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="group relative rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all cursor-pointer">
              <div className="relative h-[500px]">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20customer%20service%20representative%20making%20phone%20call%20in%20modern%20office%20headset%20and%20computer%20setup%20fast%20response%20and%20efficiency%20theme%20bright%20clean%20workspace%20simple%20background&width=400&height=600&seq=feature-inside-001&orientation=portrait"
                  alt="インサイドセールス"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-3xl font-bold text-white mb-3">5分以内コール</h3>
                  <p className="text-base text-white/90 leading-relaxed">
                    リード獲得後、5分以内に架電。6〜7回のアプローチ（電話/SMS/メール）で確実に接点を構築します。
                  </p>
                </div>
              </div>
            </div>

            <div className="group relative rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all cursor-pointer">
              <div className="relative h-[500px]">
                <img 
                  src="https://readdy.ai/api/search-image?query=confident%20business%20professional%20in%20suit%20presenting%20to%20clients%20in%20meeting%20room%20field%20sales%20and%20negotiation%20scene%20professional%20corporate%20environment%20handshake%20and%20success%20theme%20simple%20background&width=400&height=600&seq=feature-field-001&orientation=portrait"
                  alt="フィールドセールス"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-3xl font-bold text-white mb-3">トップセールス陣</h3>
                  <p className="text-base text-white/90 leading-relaxed">
                    キーエンス、リクルート、光通信出身のトップ層が商談を担当。高密度な提案で成約率を最大化します。
                  </p>
                </div>
              </div>
            </div>

            <div className="group relative rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all cursor-pointer">
              <div className="relative h-[500px]">
                <img 
                  src="https://readdy.ai/api/search-image?query=business%20handshake%20and%20contract%20signing%20scene%20success%20based%20payment%20and%20partnership%20theme%20professional%20office%20setting%20trust%20and%20agreement%20moment%20clean%20simple%20background&width=400&height=600&seq=feature-payment-001&orientation=portrait"
                  alt="成果報酬"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-3xl font-bold text-white mb-3">成果報酬対応</h3>
                  <p className="text-base text-white/90 leading-relaxed">
                    初期投資を抑えたい企業様には成果報酬プランをご用意。リスクを最小限に抑えながら加盟店開発を推進できます。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section id="results" className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <p className="text-sm font-serif text-gray-500 mb-4">Our Track Record</p>
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-2">
              圧倒的な
            </h2>
            <h2 className="text-5xl md:text-6xl font-serif italic text-gray-900 mb-4">
              実績
            </h2>
            <p className="text-lg text-gray-600">数字が証明する確実性とスピード感</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="relative bg-gradient-to-br from-orange-400 to-orange-500 rounded-3xl p-12 shadow-xl">
              <a href="#contact" className="absolute top-8 right-8 w-12 h-12 bg-white rounded-full flex items-center justify-center hover:scale-110 transition-transform cursor-pointer">
                <i className="ri-arrow-right-up-line text-2xl text-orange-500"></i>
              </a>
              <div className="text-8xl font-serif font-bold text-white mb-6">84%</div>
              <p className="text-2xl text-white font-semibold mb-4">
                アポイント獲得率
              </p>
              <p className="text-lg text-white/90 italic">
                業界平均の3倍以上の接触成功率
              </p>
            </div>

            <div className="relative bg-gradient-to-br from-purple-400 to-purple-500 rounded-3xl p-12 shadow-xl">
              <a href="#contact" className="absolute top-8 right-8 w-12 h-12 bg-white rounded-full flex items-center justify-center hover:scale-110 transition-transform cursor-pointer">
                <i className="ri-arrow-right-up-line text-2xl text-purple-500"></i>
              </a>
              <div className="text-8xl font-serif font-bold text-white mb-6">15店舗</div>
              <p className="text-2xl text-white font-semibold mb-4">
                2ヶ月で加盟達成
              </p>
              <p className="text-lg text-white/90 leading-relaxed">
                某フランチャイズ本部様の事例。売上15億円を達成し、社内ギネス記録を樹立。投資対効果は初月から黒字化を実現しました。
              </p>
            </div>
          </div>

          <div className="mt-16 grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="text-5xl font-bold text-gray-900 mb-3">6〜7回</div>
              <p className="text-base text-gray-600">徹底したアプローチ回数</p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="text-5xl font-bold text-gray-900 mb-3">5分以内</div>
              <p className="text-base text-gray-600">リード獲得後の初回架電</p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="text-5xl font-bold text-gray-900 mb-3">15億円</div>
              <p className="text-base text-gray-600">最高売上実績（社内ギネス）</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              キーエンス式高密度販売による<br />圧倒的なベネフィット
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto">
              広告とセールスの統合により、リード獲得から成約までのプロセスを最適化。<br />
              投資対効果を最大化し、確実性とスピード感を両立します。
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-speed-up-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">スピード感</h3>
              <p className="text-base text-gray-700 leading-relaxed">
                5分以内の初回架電、6〜7回の徹底アプローチにより、競合他社より圧倒的に早く商談機会を創出。機会損失を最小化します。
              </p>
            </div>

            <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">確実性</h3>
              <p className="text-base text-gray-700 leading-relaxed">
                キーエンス、リクルート、光通信出身のトップセールス陣が商談を担当。フランチャイズ特有の商談ノウハウで成約率84%を実現。
              </p>
            </div>

            <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-line-chart-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">投資対効果</h3>
              <p className="text-base text-gray-700 leading-relaxed">
                CPA偏重ではなく「成約」重視のリード定義。成果報酬対応により初期投資を抑え、確実にROIを最大化できます。
              </p>
            </div>
          </div>

          <div className="mt-16 bg-gradient-to-r from-orange-500 to-orange-600 rounded-3xl p-12 text-center shadow-2xl">
            <h3 className="text-3xl font-bold text-white mb-4">
              アントレ等のポータル依存から脱却
            </h3>
            <p className="text-xl text-white/90 leading-relaxed max-w-3xl mx-auto">
              Meta/Google広告とインサイド/フィールドセールスの両輪駆動により、<br />
              自社で質の高いリードを獲得し、確実に成約まで導きます。
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="plans" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <p className="text-sm font-serif text-gray-500 mb-4">Three Support Areas</p>
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              貴社のフェーズに合わせた
            </h2>
            <h2 className="text-5xl md:text-6xl font-serif italic text-gray-900 mb-6">
              3つの支援領域
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto">
              「リードが足りない」「アポにならない」「成約まで至らない」――。<br />
              貴社の加盟店開発におけるボトルネックをピンポイントで解消し、爆発的な成長を実現します。
            </p>
          </div>

          <div className="space-y-16">
            {/* Lead Generation */}
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-3xl overflow-hidden shadow-xl">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="p-12 flex flex-col justify-center">
                  <div className="inline-block px-4 py-2 bg-orange-500 text-white text-sm font-bold rounded-full mb-6 w-fit whitespace-nowrap">
                    Support Area 01
                  </div>
                  <h3 className="text-4xl font-bold text-gray-900 mb-4">
                    リード創出支援
                  </h3>
                  <p className="text-lg text-gray-600 mb-2 font-semibold">Lead Generation</p>
                  <p className="text-xl text-gray-800 font-bold mb-8 leading-relaxed">
                    「成約」から逆算した、<br />高感度な見込み客だけを集客
                  </p>

                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-megaphone-line text-xl text-white"></i>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-900 mb-2">Meta / Google 運用型広告の最適化</h4>
                        <p className="text-base text-gray-700 leading-relaxed">
                          ポータルサイト依存から脱却し、MetaやGoogleから能動的に情報を探している優良層へ直接アプローチ。
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-focus-3-line text-xl text-white"></i>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-900 mb-2">セールス視点のターゲット定義</h4>
                        <p className="text-base text-gray-700 leading-relaxed">
                          単に「CPA（獲得単価）」を下げるのではなく、営業現場が「商談しやすい」と感じる質の高いリードを定義。
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-palette-line text-xl text-white"></i>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-900 mb-2">クリエイティブ戦略</h4>
                        <p className="text-base text-gray-700 leading-relaxed">
                          FC検討者の心理を熟知したセールスライティングにより、広告のボラティリティ（不安定さ）を抑制。
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="relative h-full min-h-[600px]">
                  <img 
                    src="https://readdy.ai/api/search-image?query=professional digital marketing team analyzing data on multiple screens modern office workspace Meta and Google advertising campaign management bright collaborative environment clean simple background"
                    alt="リード創出支援"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                </div>
              </div>
            </div>

            {/* Inside Sales */}
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-3xl overflow-hidden shadow-xl">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="relative h-full min-h-[600px] order-2 md:order-1">
                  <img 
                    src="https://readdy.ai/api/search-image?query=energetic inside sales team making phone calls in modern call center headsets and computers fast response customer service bright professional workspace simple background"
                    alt="インサイドセールス支援"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                </div>

                <div className="p-12 flex flex-col justify-center order-1 md:order-2">
                  <div className="inline-block px-4 py-2 bg-emerald-500 text-white text-sm font-bold rounded-full mb-6 w-fit whitespace-nowrap">
                    Support Area 02
                  </div>
                  <h3 className="text-4xl font-bold text-gray-900 mb-4">
                    インサイドセールス支援
                  </h3>
                  <p className="text-lg text-gray-600 mb-2 font-semibold">Inside Sales</p>
                  <p className="text-xl text-gray-800 font-bold mb-4 leading-relaxed">
                    「5分以内」の超高速アプローチで、<br />商談機会を最大化
                  </p>

                  <div className="inline-flex items-center gap-3 px-6 py-3 bg-emerald-500 text-white font-bold rounded-full mb-8 w-fit whitespace-nowrap">
                    <i className="ri-checkbox-circle-line text-xl"></i>
                    <span>選べる契約形態：固定報酬 or 成果報酬</span>
                  </div>

                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-timer-flash-line text-xl text-white"></i>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-900 mb-2">5分以内架電 ＋ 6~7回の多面的追客</h4>
                        <p className="text-base text-gray-700 leading-relaxed">
                          リード発生後5分以内の架電を徹底。さらに電話・SMS・メールを組み合わせた高密度なアプローチで、競合他社に流れる前に繋ぎ止めます。
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-refresh-line text-xl text-white"></i>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-900 mb-2">休眠リードの掘り起こし</h4>
                        <p className="text-base text-gray-700 leading-relaxed">
                          過去に獲得して放置されているリードを「資産」に変えます。
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-line-chart-line text-xl text-white"></i>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-900 mb-2">高いアポ変換率</h4>
                        <p className="text-base text-gray-700 leading-relaxed">
                          単なる「受付」ではなく、キーエンス流のヒアリング術を用いて、見込み客の熱量を高めた状態でフィールドセールスへパスします。
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Field Sales */}
            <div className="bg-gradient-to-br from-slate-100 to-slate-200 rounded-3xl overflow-hidden shadow-xl">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="p-12 flex flex-col justify-center">
                  <div className="inline-block px-4 py-2 bg-slate-700 text-white text-sm font-bold rounded-full mb-6 w-fit whitespace-nowrap">
                    Support Area 03
                  </div>
                  <h3 className="text-4xl font-bold text-gray-900 mb-4">
                    フィールドセールス支援
                  </h3>
                  <p className="text-lg text-gray-600 mb-2 font-semibold">Field Sales</p>
                  <p className="text-xl text-gray-800 font-bold mb-8 leading-relaxed">
                    「キーエンス・リクルート流」の営業力で、<br />高単価商材を確実にクロージング
                  </p>

                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-user-star-line text-xl text-white"></i>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-900 mb-2">ハイスキル人材による商談代行</h4>
                        <p className="text-base text-gray-700 leading-relaxed">
                          キーエンス、リクルート、光通信などの出身者を中心とした「売る技術」に特化したプロフェッショナルが担当。
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-trophy-line text-xl text-white"></i>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-900 mb-2">圧倒的な実績と再現性</h4>
                        <p className="text-base text-gray-700 leading-relaxed">
                          アポ率84%、2ヶ月で15店舗加盟・売上15億円（社内ギネス）を叩き出したセールススキームを貴社に適用。
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-hand-coin-line text-xl text-white"></i>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-900 mb-2">成約率の最大化</h4>
                        <p className="text-base text-gray-700 leading-relaxed">
                          FC特有の「不安」を「確信」に変えるクロージング技術で、決定権者からの承諾を確実に引き出します。
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="relative h-full min-h-[600px]">
                  <img 
                    src="https://readdy.ai/api/search-image?query=confident field sales professional in business suit presenting to client in meeting room handshake and contract signing successful negotiation corporate office setting simple background"
                    alt="フィールドセールス支援"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 text-center bg-gradient-to-r from-gray-900 to-gray-800 rounded-3xl p-12 shadow-2xl">
            <h3 className="text-3xl font-bold text-white mb-4">
              貴社の課題に合わせて、最適な支援領域をご提案
            </h3>
            <p className="text-xl text-white/90 leading-relaxed mb-8 max-w-3xl mx-auto">
              1つの領域から、または3つすべてを統合した一気通貫支援まで。<br />
              まずは無料相談で、貴社のボトルネックを診断させてください。
            </p>
            <a href="#contact" className="inline-flex items-center gap-3 px-8 py-4 bg-orange-500 text-white font-semibold rounded-full hover:bg-orange-600 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap">
              <span className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                <i className="ri-calendar-line text-orange-500 text-xl"></i>
              </span>
              <span>無料相談を予約する</span>
              <i className="ri-arrow-right-up-line text-xl"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <p className="text-sm font-serif text-gray-500 mb-4">Our Team</p>
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-2">
              トップセールス陣による
            </h2>
            <h2 className="text-5xl md:text-6xl font-serif italic text-gray-900">
              圧倒的な成約力
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <div className="h-80 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional japanese business executive in suit confident pose corporate headshot style clean simple gray background leadership and expertise theme"
                  alt="Team Member"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6 bg-white">
                <h3 className="text-lg font-bold text-gray-900 mb-1">田中 健太</h3>
                <p className="text-sm text-gray-600">元キーエンス / セールスディレクター</p>
              </div>
            </div>

            <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <div className="h-80 bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional japanese businesswoman in business attire confident expression corporate headshot photography clean beige background sales expert theme"
                  alt="Team Member"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6 bg-white">
                <h3 className="text-lg font-bold text-gray-900 mb-1">佐藤 美咲</h3>
                <p className="text-sm text-gray-600">元リクルート / インサイドセールスマネージャー</p>
              </div>
            </div>

            <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <div className="h-80 bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional japanese business manager in formal suit authoritative presence corporate portrait style clean gray background field sales expert"
                  alt="Team Member"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6 bg-white">
                <h3 className="text-lg font-bold text-gray-900 mb-1">鈴木 大輔</h3>
                <p className="text-sm text-gray-600">元光通信 / フィールドセールスリーダー</p>
              </div>
            </div>

            <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <div className="h-80 bg-gradient-to-br from-stone-200 to-stone-300 flex items-center justify-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional japanese marketing specialist in business casual attire friendly confident look corporate headshot clean brown background advertising expert"
                  alt="Team Member"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6 bg-white">
                <h3 className="text-lg font-bold text-gray-900 mb-1">山田 愛子</h3>
                <p className="text-sm text-gray-600">広告運用スペシャリスト / 認定資格保有</p>
              </div>
            </div>
          </div>

          <div className="mt-16 bg-white rounded-3xl p-12 shadow-lg">
            <div className="max-w-3xl mx-auto text-center">
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                キーエンス式高密度販売の実践者たち
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                当社のセールスチームは、キーエンス、リクルート、光通信といった営業力で知られる企業出身のトップ層で構成されています。フランチャイズ特有の商談ノウハウと、徹底した顧客フォローにより、業界最高水準の成約率を実現しています。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section id="contact" className="relative py-32 bg-gradient-to-br from-slate-800 to-slate-900">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(https://readdy.ai/api/search-image?query=successful%20business%20handshake%20in%20bright%20modern%20office%20partnership%20and%20collaboration%20scene%20warm%20natural%20lighting%20professional%20corporate%20environment%20trust%20and%20agreement%20moment&width=1920&height=1080&seq=cta-bg-001&orientation=landscape)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}></div>
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-8 tracking-wide leading-tight">
            まずは無料相談から<br />始めませんか？
          </h2>
          
          <p className="text-2xl text-white/90 mb-4 leading-relaxed">
            投資対効果を最大化する加盟店開発戦略を
          </p>
          <p className="text-2xl text-white/90 mb-12 leading-relaxed">
            30分の無料コンサルティングでご提案します
          </p>
          
          <a href="mailto:contact@example.com" className="inline-flex items-center gap-3 px-10 py-5 bg-orange-500 text-white text-lg font-semibold rounded-full hover:bg-orange-600 transition-all shadow-2xl hover:shadow-3xl cursor-pointer whitespace-nowrap">
            <span className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
              <i className="ri-calendar-line text-orange-500 text-2xl"></i>
            </span>
            <span>今すぐ無料相談を予約</span>
            <i className="ri-arrow-right-up-line text-2xl"></i>
          </a>

          <p className="mt-8 text-base text-white/70 font-light">
            ※ 所要時間30分 / オンライン対応可 / 無理な営業は一切ありません
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://static.readdy.ai/image/b493beaa830f64c0e7708d562b6b5fcb/da713bd2611879be8599d7bc969c0c05.jpeg" 
                  alt="Logo" 
                  className="h-10 w-auto object-contain"
                />
              </div>
              <p className="text-sm text-gray-400 leading-relaxed">
                フランチャイズ本部向け営業支援一気通貫サービス
              </p>
            </div>

            <div>
              <h4 className="text-base font-bold mb-4">サービス</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#features" className="hover:text-white transition-colors cursor-pointer">広告運用</a></li>
                <li><a href="#features" className="hover:text-white transition-colors cursor-pointer">インサイドセールス</a></li>
                <li><a href="#features" className="hover:text-white transition-colors cursor-pointer">フィールドセールス</a></li>
                <li><a href="#plans" className="hover:text-white transition-colors cursor-pointer">料金プラン</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-base font-bold mb-4">企業情報</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#results" className="hover:text-white transition-colors cursor-pointer">実績</a></li>
                <li><a href="#faq" className="hover:text-white transition-colors cursor-pointer">よくある質問</a></li>
                <li><a href="#contact" className="hover:text-white transition-colors cursor-pointer">お問い合わせ</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">プライバシーポリシー</a></li>
              </ul>
            </div>

            <div className="text-right">
              <p className="text-base font-serif mb-2">© 2025 フランチャイズ営業支援</p>
              <p className="text-sm text-gray-400 mb-4">All rights reserved</p>
              <div className="flex justify-end gap-4">
                <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-twitter-x-line text-xl"></i>
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-facebook-line text-xl"></i>
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-linkedin-line text-xl"></i>
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="pt-8 border-t border-gray-800 text-center">
            <p className="text-sm text-gray-400">
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">
                Powered by Readdy
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
